import React from 'react';
import { CreditCard, Calendar, Percent, DollarSign, Globe } from 'lucide-react';
import { formatNumber } from '../../../../../utils/formatting';

const MortgageParametersSection = ({ 
  formData, 
  handleInputChange, 
  errors, 
  supportedCurrencies,
  currencyRates
}) => {
  // Получение символа выбранной валюты
  const getCurrencySymbol = () => {
    const currency = supportedCurrencies.find(c => c.id === formData.currency);
    return currency ? currency.symbol : formData.currency;
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
      {/* Сумма кредита */}
      <div>
        <label htmlFor="loanAmount" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 flex items-center">
          <CreditCard className="w-4 h-4 mr-2 text-indigo-500" />
          Сумма кредита ({formData.currency})
        </label>
        <div className="relative mt-1">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <span className="text-gray-500 sm:text-sm">{getCurrencySymbol()}</span>
          </div>
          <input
            type="text"
            id="loanAmount"
            name="loanAmount"
            value={formData.loanAmount}
            onChange={handleInputChange}
            className={`form-input pl-7 w-full ${errors.loanAmount ? 'input-error' : ''}`}
            placeholder="Введите сумму"
          />
        </div>
        {errors.loanAmount && (
          <p className="mt-1 text-xs text-red-500 dark:text-red-400">
            {errors.loanAmount}
          </p>
        )}
        <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
          ≈ {formatNumber(parseFloat(formData.equivalentInRubles || 0))} ₽ по текущему курсу
        </p>
      </div>

      {/* Валюта кредита */}
      <div>
        <label htmlFor="currency" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 flex items-center">
          <Globe className="w-4 h-4 mr-2 text-indigo-500" />
          Валюта кредита
        </label>
        <select
          id="currency"
          name="currency"
          value={formData.currency}
          onChange={handleInputChange}
          className={`form-select w-full ${errors.currency ? 'input-error' : ''}`}
        >
          {supportedCurrencies.map((currency) => (
            <option key={currency.id} value={currency.id}>
              {currency.name} ({currency.id}) - {currency.symbol}
            </option>
          ))}
        </select>
        {errors.currency && (
          <p className="mt-1 text-xs text-red-500 dark:text-red-400">
            {errors.currency}
          </p>
        )}
        <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
          Текущий курс: 1 {formData.currency} = {formatNumber(currencyRates[formData.currency] || 0)} ₽
        </p>
      </div>

      {/* Первоначальный взнос */}
      <div>
        <label htmlFor="initialDownPayment" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 flex items-center">
          <DollarSign className="w-4 h-4 mr-2 text-indigo-500" />
          Первоначальный взнос (₽)
        </label>
        <div className="relative mt-1">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <span className="text-gray-500 sm:text-sm">₽</span>
          </div>
          <input
            type="text"
            id="initialDownPayment"
            name="initialDownPayment"
            value={formData.initialDownPayment}
            onChange={handleInputChange}
            className={`form-input pl-7 w-full ${errors.initialDownPayment ? 'input-error' : ''}`}
            placeholder="Введите сумму в рублях"
          />
        </div>
        {errors.initialDownPayment && (
          <p className="mt-1 text-xs text-red-500 dark:text-red-400">
            {errors.initialDownPayment}
          </p>
        )}
        <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
          ≈ {formData.currency && currencyRates[formData.currency] 
            ? formatNumber(parseFloat(formData.initialDownPayment || 0) / currencyRates[formData.currency], 2) 
            : '0'} {formData.currency}
        </p>
      </div>

      {/* Срок кредита */}
      <div>
        <label htmlFor="loanTerm" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 flex items-center">
          <Calendar className="w-4 h-4 mr-2 text-indigo-500" />
          Срок кредита (лет)
        </label>
        <input
          type="text"
          id="loanTerm"
          name="loanTerm"
          value={formData.loanTerm}
          onChange={handleInputChange}
          className={`form-input w-full ${errors.loanTerm ? 'input-error' : ''}`}
          placeholder="Введите срок"
        />
        {errors.loanTerm && (
          <p className="mt-1 text-xs text-red-500 dark:text-red-400">
            {errors.loanTerm}
          </p>
        )}
      </div>

      {/* Процентная ставка */}
      <div>
        <label htmlFor="interestRate" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 flex items-center">
          <Percent className="w-4 h-4 mr-2 text-indigo-500" />
          Процентная ставка (%)
        </label>
        <input
          type="text"
          id="interestRate"
          name="interestRate"
          value={formData.interestRate}
          onChange={handleInputChange}
          className={`form-input w-full ${errors.interestRate ? 'input-error' : ''}`}
          placeholder="Введите ставку"
        />
        {errors.interestRate && (
          <p className="mt-1 text-xs text-red-500 dark:text-red-400">
            {errors.interestRate}
          </p>
        )}
      </div>

      {/* Дата начала кредита */}
      <div>
        <label htmlFor="startDate" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
          Дата начала кредита
        </label>
        <input
          type="date"
          id="startDate"
          name="startDate"
          value={formData.startDate}
          onChange={handleInputChange}
          className={`form-input w-full ${errors.startDate ? 'input-error' : ''}`}
        />
        {errors.startDate && (
          <p className="mt-1 text-xs text-red-500 dark:text-red-400">
            {errors.startDate}
          </p>
        )}
      </div>

      {/* Тип платежа */}
      <div className="col-span-full">
        <div className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Тип платежа
        </div>
        <div className="grid grid-cols-2 gap-2">
          <label className="inline-flex items-center">
            <input
              type="radio"
              name="paymentType"
              value="annuity"
              checked={formData.paymentType === 'annuity'}
              onChange={handleInputChange}
              className="form-radio"
            />
            <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">
              Аннуитетный (равные платежи)
            </span>
          </label>
          <label className="inline-flex items-center">
            <input
              type="radio"
              name="paymentType"
              value="differentiated"
              checked={formData.paymentType === 'differentiated'}
              onChange={handleInputChange}
              className="form-radio"
            />
            <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">
              Дифференцированный (уменьшающийся)
            </span>
          </label>
        </div>
        {errors.paymentType && (
          <p className="mt-1 text-xs text-red-500 dark:text-red-400">
            {errors.paymentType}
          </p>
        )}
      </div>
    </div>
  );
};

export default MortgageParametersSection;